import * as React from "react";
import * as Sentry from "@sentry/react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { useToggle } from "react-use";
import { useCurrentPdf } from "@/modules/chat/hooks/useCurrentPdf";
import { CitationInfo } from "@/modules/chat/chat-ui.types";
import { HighlightArea } from "@/components/feature/pdf/types";
import { PdfDocumentViewer, PdfDocumentViewerHandle } from "@/components/feature/pdf/pdfDocumentViewer";
import { PdfToolbar } from "@/components/feature/pdf/pdfToolbar";
import { pdfUrlFor } from "@/components/feature/pdf/helpers";

interface PDFViewerProps {
  citationHighlight: CitationInfo | null;
}

const ZOOM_STEP = 1.1;
const MIN_ZOOM = 0.2;
const MAX_ZOOM = 5;

// Keep the N most recently viewed documents mounted so switching back to one
// (e.g. bouncing between cited PDFs during Q&A) reuses its already-fetched,
// already-parsed pdf.js document instead of re-downloading and re-parsing.
// react-pdf destroys a document's PDFDocumentProxy on unmount, so the only way
// to retain it is to keep the <Document> mounted; inactive ones are hidden with
// display:none. The bound caps memory (each retained doc holds its proxy plus
// any rendered page canvases).
const MAX_CACHED_DOCS = 5;

const EMPTY_HIGHLIGHTS: HighlightArea[] = [];

const pruneMap = <T,>(map: Map<string, T>, keep: string[]): Map<string, T> => {
  const keepSet = new Set(keep);
  let changed = false;
  for (const key of map.keys()) {
    if (!keepSet.has(key)) {
      changed = true;
      break;
    }
  }
  if (!changed) return map;
  const next = new Map<string, T>();
  for (const [key, value] of map) {
    if (keepSet.has(key)) next.set(key, value);
  }
  return next;
};

export const PDFViewer: React.FC<PDFViewerProps> = ({ citationHighlight }) => {
  const { currentPdfId } = useCurrentPdf();
  const [openDocs, setOpenDocs] = React.useState<string[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [areas, setAreas] = React.useState<HighlightArea[]>([]);
  const [proxies, setProxies] = React.useState<Map<string, PDFDocumentProxy>>(new Map());
  const [pages, setPages] = React.useState<Map<string, number>>(new Map());
  const [zoom, setZoom] = React.useState(1);
  const [rotation, setRotation] = React.useState(0);
  const [searchOpen, toggleSearchOpen] = useToggle(false);
  const [searchText, setSearchText] = React.useState("");
  const [matchPages, setMatchPages] = React.useState<number[]>([]);
  const [activeMatchIndex, setActiveMatchIndex] = React.useState(0);
  const [isFullscreen, setIsFullscreen] = React.useState(false);
  const [fullscreenError, setFullscreenError] = React.useState<string | null>(null);
  const rootRef = React.useRef<HTMLDivElement | null>(null);
  const lastAppliedCitationRef = React.useRef<CitationInfo | null>(null);
  const viewerRefs = React.useRef<Map<string, PdfDocumentViewerHandle>>(new Map());

  const activeProxy = currentPdfId ? (proxies.get(currentPdfId) ?? null) : null;
  const currentPage = currentPdfId ? (pages.get(currentPdfId) ?? 0) : 0;

  // Render the active doc on the first paint even before the LRU effect below
  // has folded it into openDocs, so selecting a document never flashes empty.
  const docsToRender =
    currentPdfId && !openDocs.includes(currentPdfId) ? [currentPdfId, ...openDocs].slice(0, MAX_CACHED_DOCS) : openDocs;

  const zoomIn = React.useCallback(() => setZoom(value => Math.min(value * ZOOM_STEP, MAX_ZOOM)), []);
  const zoomOut = React.useCallback(() => setZoom(value => Math.max(value / ZOOM_STEP, MIN_ZOOM)), []);
  const fitWidth = React.useCallback(() => setZoom(1), []);

  React.useEffect(() => {
    const syncFullscreenState = () => setIsFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener("fullscreenchange", syncFullscreenState);
    return () => document.removeEventListener("fullscreenchange", syncFullscreenState);
  }, []);

  const toggleFullscreen = React.useCallback(async () => {
    setFullscreenError(null);
    try {
      if (rootRef.current && document.fullscreenElement !== rootRef.current) {
        await rootRef.current.requestFullscreen();
      } else if (document.fullscreenElement === rootRef.current) {
        await document.exitFullscreen();
      }
    } catch (fullscreenRequestError) {
      Sentry.captureException(fullscreenRequestError);
      setFullscreenError("Unable to change fullscreen mode.");
    }
  }, []);

  React.useEffect(() => {
    if (!currentPdfId) return;
    setOpenDocs(previous => {
      const next = [currentPdfId, ...previous.filter(id => id !== currentPdfId)].slice(0, MAX_CACHED_DOCS);
      if (next.length === previous.length && next.every((id, index) => id === previous[index])) {
        return previous;
      }
      return next;
    });
  }, [currentPdfId]);

  React.useEffect(() => {
    setProxies(previous => pruneMap(previous, openDocs));
    setPages(previous => pruneMap(previous, openDocs));
    for (const id of [...viewerRefs.current.keys()]) {
      if (!openDocs.includes(id)) viewerRefs.current.delete(id);
    }
  }, [openDocs]);

  React.useEffect(() => {
    setError(null);
    setAreas([]);
    setSearchText("");
    setMatchPages([]);
    lastAppliedCitationRef.current = null;
  }, [currentPdfId]);

  const handleDocumentLoad = React.useCallback((fseId: string, pdf: PDFDocumentProxy) => {
    setProxies(previous => new Map(previous).set(fseId, pdf));
  }, []);

  const handleLoadError = React.useCallback((loadError: Error) => {
    Sentry.captureException(loadError);
    setError("PDF unavailable for this document.");
  }, []);

  const handlePageChange = React.useCallback((fseId: string, pageIndex: number) => {
    setPages(previous => {
      if (previous.get(fseId) === pageIndex) return previous;
      return new Map(previous).set(fseId, pageIndex);
    });
  }, []);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!(event.metaKey || event.ctrlKey)) return;
    switch (event.key) {
      case "f":
        event.preventDefault();
        toggleSearchOpen(true);
        break;
      case "=":
      case "+":
        event.preventDefault();
        zoomIn();
        break;
      case "-":
        event.preventDefault();
        zoomOut();
        break;
      case "0":
        event.preventDefault();
        fitWidth();
        break;
    }
  };

  React.useEffect(() => {
    if (
      activeProxy &&
      citationHighlight &&
      citationHighlight.fseId === currentPdfId &&
      (!lastAppliedCitationRef.current ||
        JSON.stringify(lastAppliedCitationRef.current) !== JSON.stringify(citationHighlight))
    ) {
      const allHighlights: HighlightArea[] = citationHighlight.highlights.map(h => ({
        pageIndex: h.page,
        left: h.boundingRect.left,
        top: h.boundingRect.top,
        width: h.boundingRect.width,
        height: h.boundingRect.height,
      }));

      setAreas(allHighlights);
      if (allHighlights.length > 0) {
        viewerRefs.current.get(currentPdfId)?.jumpToArea(allHighlights[0]);
      } else if (citationHighlight.page !== undefined) {
        viewerRefs.current.get(currentPdfId)?.jumpToPage(Math.max(citationHighlight.page - 1, 0));
      }
      lastAppliedCitationRef.current = citationHighlight;
    }
  }, [citationHighlight, currentPdfId, activeProxy]);

  React.useEffect(() => {
    if (!activeProxy || !searchText) {
      setMatchPages([]);
      setActiveMatchIndex(0);
      return;
    }
    let cancelled = false;
    const scan = async () => {
      const needle = searchText.toLowerCase();
      const found: number[] = [];
      for (let pageNumber = 1; pageNumber <= activeProxy.numPages; pageNumber++) {
        const page = await activeProxy.getPage(pageNumber);
        const content = await page.getTextContent();
        const text = content.items.map(item => ("str" in item ? item.str : "")).join(" ");
        if (text.toLowerCase().includes(needle)) {
          found.push(pageNumber - 1);
        }
        if (cancelled) return;
      }
      if (!cancelled) {
        setMatchPages(found);
        setActiveMatchIndex(0);
        if (found.length > 0 && currentPdfId) {
          viewerRefs.current.get(currentPdfId)?.jumpToPage(found[0]);
        }
      }
    };
    void scan();
    return () => {
      cancelled = true;
    };
  }, [activeProxy, searchText, currentPdfId]);

  const handleJumpToMatch = (matchIndex: number) => {
    setActiveMatchIndex(matchIndex);
    const pageIndex = matchPages[matchIndex];
    if (pageIndex !== undefined && currentPdfId) {
      viewerRefs.current.get(currentPdfId)?.jumpToPage(pageIndex);
    }
  };

  if (error) {
    return (
      <div role="alert" className="flex h-full items-center justify-center px-4 text-center text-red-700">
        {error}
      </div>
    );
  }

  if (!currentPdfId) {
    return <div className="flex h-full items-center justify-center text-gray-500">No document selected.</div>;
  }

  return (
    <div
      ref={rootRef}
      className="flex h-full min-h-0 flex-1 flex-col bg-gray-50"
      tabIndex={0}
      onKeyDown={handleKeyDown}
    >
      <PdfToolbar
        numPages={activeProxy?.numPages ?? 0}
        currentPage={currentPage}
        onJumpToPage={pageIndex => viewerRefs.current.get(currentPdfId)?.jumpToPage(pageIndex)}
        onZoomIn={zoomIn}
        onZoomOut={zoomOut}
        onFitWidth={fitWidth}
        onRotate={() => setRotation(value => (value + 90) % 360)}
        searchOpen={searchOpen}
        onToggleSearch={toggleSearchOpen}
        searchText={searchText}
        onSearch={setSearchText}
        matchPages={matchPages}
        activeMatchIndex={activeMatchIndex}
        onJumpToMatch={handleJumpToMatch}
        isFullscreen={isFullscreen}
        onToggleFullscreen={() => void toggleFullscreen()}
        portalContainer={isFullscreen ? (rootRef.current ?? undefined) : undefined}
      />
      {fullscreenError && (
        <div role="alert" className="border-b border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {fullscreenError}
        </div>
      )}
      <div className="relative min-h-0 flex-1">
        {docsToRender.map(fseId => {
          const isActive = fseId === currentPdfId;
          return (
            <div key={fseId} className="absolute inset-0 flex flex-col" style={{ display: isActive ? "flex" : "none" }}>
              <PdfDocumentViewer
                ref={handle => {
                  if (handle) viewerRefs.current.set(fseId, handle);
                  else viewerRefs.current.delete(fseId);
                }}
                fileUrl={pdfUrlFor(fseId)}
                highlights={isActive ? areas : EMPTY_HIGHLIGHTS}
                rotation={rotation}
                zoom={zoom}
                searchText={isActive ? searchText : ""}
                onDocumentLoad={pdf => handleDocumentLoad(fseId, pdf)}
                onLoadError={isActive ? handleLoadError : undefined}
                onCurrentPageChange={pageIndex => handlePageChange(fseId, pageIndex)}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
};
